import { z } from 'zod';
/**
 * PUCT §25.478 deposit-waiver matrix — E-SF05 AC1, as FIRST-CLASS RULES.
 *
 * ── WHY A RULE SET, NOT FREE TEXT ──────────────────────────────────────────
 * AC1 requires the deposit screen to implement the §25.478 waiver grounds "as
 * first-class rules", not a free-text "reason" box. A free-text field cannot be
 * validated, cannot drive a distinct downstream effect (a full waiver vs a
 * two-installment split vs a guarantor substitution behave differently), and
 * cannot carry the certification requirement each ground has. So each ground is
 * an enum member with typed metadata: its EFFECT, whether it needs a
 * CERTIFICATION, and its citation. The FE renders one selectable rule per ground;
 * the BFF validates the election against this same table. There is one source of
 * truth for "what waivers exist", shared across the edge.
 *
 * ── LEGAL ACCURACY NOTE ────────────────────────────────────────────────────
 * The grounds and their identifiers are taken verbatim from E-SF05 AC1 (age 62+,
 * family-violence certification, medical indigence, low-income two-installment
 * split, guarantor stub). The section/subsection citations below are the working
 * PUCT §25.478 references; they are marked for legal confirmation before the
 * screen is presented to a real customer (this whole storefront is pre-publish —
 * see STUBS.md). The ARCHITECTURE this file fixes — first-class, typed, distinct
 * effects — is what AC1 turns on and does not depend on the exact citation text.
 *
 * ⚠️ A waiver is the ABSENCE of a charge. Recording one must be as deliberate as
 * collecting money: a silently-granted waiver is a compliance violation, not a
 * convenience (US fail-loud posture). The BFF records the election; it never
 * infers one.
 */
/** The five §25.478 grounds, first-class. `GUARANTOR` is a stub (see metadata). */
export const WaiverGround = z.enum([
    /** Customer is 62 years of age or older (§25.478(e)). */
    'AGE_62_PLUS',
    /** Certified victim of family violence (§25.478(e)) — needs an agency certification. */
    'FAMILY_VIOLENCE',
    /** Medical indigence / seriously-ill household member (§25.478 / §25.480 read-together). */
    'MEDICAL_INDIGENCE',
    /** Low-income customer electing the statutory two-installment split (§25.478(g)). */
    'LOW_INCOME_INSTALLMENT',
    /** A qualified guarantor stands in lieu of the deposit (§25.478(f)) — STUB this slice. */
    'GUARANTOR',
]);
/**
 * What electing a ground DOES:
 *  - `FULL_WAIVER`            — no deposit is owed.
 *  - `INSTALLMENT_SPLIT`      — the deposit is owed but paid in the statutory
 *                               instalments (not waived) — the low-income path.
 *  - `GUARANTOR_SUBSTITUTION` — a third party guarantees the deposit instead.
 */
export const WaiverEffect = z.enum(['FULL_WAIVER', 'INSTALLMENT_SPLIT', 'GUARANTOR_SUBSTITUTION']);
/** What certification a ground requires before it can be honoured. */
export const CertificationKind = z.enum([
    'NONE',
    /** Self-attested age; ID verified at a later KYC step, not here. */
    'SELF_ATTESTED_AGE',
    /** Family-violence certification letter (agency / court). */
    'FAMILY_VIOLENCE_CERTIFICATION',
    /** Physician / hospital statement of serious illness. */
    'MEDICAL_STATEMENT',
    /** Low-income program enrolment (e.g. LIHEAP) or self-attestation per tariff. */
    'LOW_INCOME_PROOF',
    /** A guarantor agreement — stubbed this slice. */
    'GUARANTOR_AGREEMENT',
]);
/** The statutory two-installment split terms (§25.478(g)), carried as data. */
export const InstallmentPlanSchema = z.object({
    /** First instalment, as a percentage of the deposit (statutory: 50%). */
    firstInstalmentPercent: z.number().int().min(1).max(100),
    /** Days from the decision the first instalment is due. */
    firstInstalmentDueDays: z.number().int().nonnegative(),
    /** Days from the decision the remainder is due. */
    remainderDueDays: z.number().int().nonnegative(),
});
/**
 * The statutory low-income split: half up front within 10 days, the remainder by
 * day 40 (per ADR-0009's summary of §25.478(g)). Carried as data so the screen
 * and the BFF agree on the terms, and a later tariff change is one edit here.
 */
const LOW_INCOME_SPLIT = {
    firstInstalmentPercent: 50,
    firstInstalmentDueDays: 10,
    remainderDueDays: 40,
};
/**
 * The canonical matrix. Ordered as the screen presents it. `as const`-shaped via
 * an explicit type so a missing/extra ground is a compile error, not a runtime
 * surprise.
 */
export const WAIVER_MATRIX = [
    {
        ground: 'AGE_62_PLUS',
        effect: 'FULL_WAIVER',
        certification: 'SELF_ATTESTED_AGE',
        isStub: false,
        citation: 'PUCT §25.478(e)',
        i18nKey: 'age62',
    },
    {
        ground: 'FAMILY_VIOLENCE',
        effect: 'FULL_WAIVER',
        certification: 'FAMILY_VIOLENCE_CERTIFICATION',
        isStub: false,
        citation: 'PUCT §25.478(e)',
        i18nKey: 'familyViolence',
    },
    {
        ground: 'MEDICAL_INDIGENCE',
        effect: 'FULL_WAIVER',
        certification: 'MEDICAL_STATEMENT',
        isStub: false,
        citation: 'PUCT §25.478 / §25.480',
        i18nKey: 'medicalIndigence',
    },
    {
        ground: 'LOW_INCOME_INSTALLMENT',
        effect: 'INSTALLMENT_SPLIT',
        certification: 'LOW_INCOME_PROOF',
        isStub: false,
        citation: 'PUCT §25.478(g)',
        i18nKey: 'lowIncomeInstallment',
        installment: LOW_INCOME_SPLIT,
    },
    {
        ground: 'GUARANTOR',
        effect: 'GUARANTOR_SUBSTITUTION',
        certification: 'GUARANTOR_AGREEMENT',
        isStub: true,
        citation: 'PUCT §25.478(f)',
        i18nKey: 'guarantor',
    },
];
/** Lookup a rule by ground. Returns undefined for an unknown ground. */
export function waiverRuleFor(ground) {
    return WAIVER_MATRIX.find((r) => r.ground === ground);
}
/**
 * A customer's waiver election (E-SF05 AC2 — "waiver election recorded").
 *
 * ── WHY `acknowledged: literal(true)` ──────────────────────────────────────
 * A waiver election is a legal attestation, not a UI toggle. The customer must
 * affirmatively acknowledge the ground applies to them and that a false
 * attestation carries consequences. `z.literal(true)` makes "recorded without
 * acknowledgement" un-representable in the contract, not merely discouraged.
 *
 * `certificationRef` is a free reference the customer supplies for grounds that
 * need proof (a case number, a program id) — deliberately bounded and treated as
 * PII-adjacent on the request path (the BFF redacts it and never echoes it).
 */
export const WaiverElectionSchema = z.object({
    ground: WaiverGround,
    acknowledged: z.literal(true),
    /** Optional certification reference for grounds requiring proof. */
    certificationRef: z.string().min(1).max(120).optional(),
});
//# sourceMappingURL=waiver.js.map